import type { QueryClient } from "@tanstack/react-query";

import {
  invalidateDashboardAffectedQueries,
  isDashboardAffectedQueryKey,
} from "@/lib/query/dashboard-affected-queries";

/**
 * Request-path fragments an acknowledgement write can change, beyond the
 * dashboard queries that `dashboard-affected-queries` always adds.
 *
 * `/acknowledgements` matches every surface in one fragment: the staff member's
 * own outstanding list and history (`/v1/nurseries/{id}/acknowledgements/me/…`),
 * the manager's item list and per-item tracking view
 * (`/v1/acknowledgements/items/…`), the per-person view
 * (`…/acknowledgements/staff/{id}`) and the reader
 * (`/v1/acknowledgements/requests/{id}`).
 *
 * `/event-records` is here too, and it is load-bearing rather than defensive:
 * an outstanding acknowledgement request is what keeps an incident in the Events
 * Hub's "awaiting acknowledgement" tab, so signing one moves the record between
 * tabs. Each tab is its own query key, and the app's global `staleTime` is five
 * minutes — without this the Hub would keep showing the record in the tab it
 * just left.
 *
 * NOTE: the staff member's To do list is NOT reached by this helper. That list is
 * an offline-first Dexie hook, not a React Query, so it refreshes through the
 * sync service instead. Do not "simplify" the optimistic cache update in
 * use-offline-acknowledgement-sign on the assumption that this covers it.
 */
const ACKNOWLEDGEMENT_AFFECTED_KEY_PATHS = [
  "/acknowledgements",
  "/event-records",
] as const;

/**
 * True when a React Query key belongs to a resource an acknowledgement write can
 * change. Exported for the drift-guard test.
 */
export function isAcknowledgementAffectedQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return isDashboardAffectedQueryKey(
    queryKey,
    ACKNOWLEDGEMENT_AFFECTED_KEY_PATHS,
  );
}

/**
 * Invalidate every query that renders data an acknowledgement write can change.
 */
export async function invalidateAcknowledgementQueries(
  queryClient: QueryClient,
): Promise<void> {
  await invalidateDashboardAffectedQueries(queryClient, {
    keyPaths: ACKNOWLEDGEMENT_AFFECTED_KEY_PATHS,
  });
}
